import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { OrderContext } from './OrderContext';
import { formatPageTitle, titleSegmentForPathname } from '../utils/pageTitle';

const PageTitleOverrideContext = createContext<(segment: string | null) => void>(() => {});

/** Sets a more specific tab title than the route default (order number, shipment name, SKU). */
export function usePageTitle(segment: string): void {
  const setOverride = useContext(PageTitleOverrideContext);

  useEffect(() => {
    setOverride(segment);
    return () => setOverride(null);
  }, [segment, setOverride]);
}

interface PageTitleProviderProps {
  readonly children: React.ReactNode;
}

export function PageTitleProvider({ children }: PageTitleProviderProps): React.ReactElement {
  const { authenticated, loading } = useAuth();
  const location = useLocation();
  const orderCtx = useContext(OrderContext);
  const [override, setOverride] = useState<string | null>(null);
  const pathRef = useRef(location.pathname);

  if (pathRef.current !== location.pathname) {
    pathRef.current = location.pathname;
    if (override !== null) {
      setOverride(null);
    }
  }

  let segment: string;
  if (loading) {
    segment = '';
  } else if (!authenticated) {
    segment = 'Login';
  } else {
    segment = override ?? titleSegmentForPathname(location.pathname, {
      orderNumber: orderCtx?.orderNumberCin7,
    });
  }

  useEffect(() => {
    document.title = formatPageTitle(segment);
  }, [segment]);

  return (
    <PageTitleOverrideContext.Provider value={setOverride}>
      {children}
    </PageTitleOverrideContext.Provider>
  );
}
